// Taki taslaklarinin fiyatini urunun GUNCEL maliyet + kargosuyla yeniden hesaplar.
//
// Kural Liste Denetimi ile ayni: satis, tum Etsy ucretleri dusuldukten sonra
// hedef net kar % kalacak sekilde (eski taslaklarda (maliyet + kargo) x carpan);
// Etsy'ye giden liste fiyati = satis x LIST_PRICE_FACTOR. Taslagin varyasyon yapisi
// (eksenler, cikarilan secenekler, sku) KORUNUR; yalnizca fiyatlar degisir.
// Fiyatli eksenin yeni degerleri buildVariations'tan etikete gore eslenir.

import type { ProductCategory } from "@erc/shared";
import {
  DEFAULT_TARGET_MARGIN, LIST_PRICE_FACTOR, marginRule, multiplierRule, etsyProfit,
  productCost, productShipping, resolveOptionSets, effectiveOptionRefs, buildVariations,
  entryComboPrices, scaleVariationPrices,
  type TemplateRow, type OptionSet, type EntryVariant, type PlannedVariation, type PriceRule,
} from "./jewelryListing";

/** Tam taslak (GET /api/listing-drafts, light olmadan) — kullanilan alanlar */
export interface FullDraft {
  id: string;
  title: string;
  status: string;
  price: number | null;
  templateId: string | null;
  etsyListingId: string | null;
  shopId: string | null;
  imageUrls?: string[] | null;
  variations: PlannedVariation[] | null;
  attributes: Record<string, unknown> | null;
}

interface Pricing {
  marginPct?: number; multiplier?: number; commissionPercent?: number; salePrice?: number; profit?: number;
  listPriceFactor?: number; listPrice?: number;
  entryVariant?: EntryVariant | null; entryPrice?: number | null; entryProfit?: number | null;
}
interface Supplier { catalogId?: string; categoryId?: string; cost?: number; shipping?: number; [k: string]: unknown }

/** "draft": taslakta kayitli kural (hedef kar ya da eski carpan); "rule": guncel hedef kar (sablon, yoksa varsayilan) */
export type MultiplierSource = "draft" | "rule";

export interface PricePlan {
  draft: FullDraft;
  /** Hesaplanamadiysa sebebi; plan uygulanmaz */
  skip?: string;
  oldCost: number | null; newCost: number;
  oldShipping: number | null; newShipping: number;
  /** Fiyat kurali etiketi: "%20 kâr" ya da eski "×1.5" */
  oldRule: string | null; newRule: string;
  oldPrice: number | null; newPrice: number;
  oldRange: [number, number] | null; newRange: [number, number] | null;
  oldEntry: number | null; newEntry: number | null;
  profit: number; marginPct: number; entryProfit: number | null;
  /** Fiyati eslenemeyen varyasyon secenekleri (eski fiyatla kalir) */
  unmatched: number;
  changed: boolean;
  patch: { price: number; variations: PlannedVariation[] | null; attributes: Record<string, unknown> };
}

const r2 = (n: number) => Math.round(n * 100) / 100;
const differs = (a: number | null | undefined, b: number | null | undefined) =>
  (a == null) !== (b == null) || (a != null && b != null && Math.abs(a - b) > 0.005);

/** Varyasyonlardaki acik fiyatlarin araligi */
function priceRange(vars: PlannedVariation[] | null | undefined): [number, number] | null {
  const ps = (vars ?? []).flatMap((v) => v.options.map((o) => o.sellingPrice)).filter((p): p is number => typeof p === "number");
  return ps.length ? [Math.min(...ps), Math.max(...ps)] : null;
}

export function findProduct(
  catalogs: Array<{ id: string; categories: unknown[] }>, catalogId?: string, categoryId?: string,
): ProductCategory | null {
  if (!categoryId) return null;
  const cat = catalogs.find((c) => c.id === catalogId)
    ?? catalogs.find((c) => (c.categories as Array<{ id: string }>).some((p) => p.id === categoryId));
  return ((cat?.categories ?? []) as ProductCategory[]).find((p) => p.id === categoryId) ?? null;
}

export function planPriceUpdate(input: {
  draft: FullDraft;
  product: ProductCategory | null;
  templates: TemplateRow[];
  optionSets: OptionSet[];
  multiplierSource: MultiplierSource;
}): PricePlan {
  const { draft, product, templates, optionSets, multiplierSource } = input;
  const attrs = draft.attributes ?? {};
  const pricing = (attrs.pricing ?? {}) as Pricing;
  const supplier = (attrs.supplier ?? {}) as Supplier;
  const template = templates.find((t) => t.id === draft.templateId) ?? null;
  const rules = template?.attributes ?? {};

  const ruleMargin = rules.targetMarginPct ?? DEFAULT_TARGET_MARGIN;
  // Taslaktaki kural: hedef kar; yoksa eski carpan; o da yoksa guncel kural
  const oldRule = pricing.marginPct != null ? `%${pricing.marginPct} kâr` : pricing.multiplier ? `×${pricing.multiplier}` : null;
  const keepDraft = multiplierSource === "draft";
  const useMargin = keepDraft && pricing.marginPct != null ? pricing.marginPct
    : keepDraft && pricing.multiplier ? null
    : ruleMargin;
  const rule: PriceRule = useMargin != null ? marginRule(useMargin) : multiplierRule(pricing.multiplier!);
  // Eski taslaklar x2 kuralindan once olusturuldu: fiyatlari satis fiyatidir
  const oldFactor = pricing.listPriceFactor ?? 1;

  const cost = productCost(product, true);
  const shipping = productShipping(product, true);
  const salePrice = rule.price(cost + shipping);
  const listPrice = r2(salePrice * LIST_PRICE_FACTOR);
  const analysis = etsyProfit(salePrice, cost, shipping);
  const profit = analysis.net;

  // Guncel fiyatli eksen (satis fiyatlariyla) — etikete gore eslemek icin
  const { variationSets } = resolveOptionSets(effectiveOptionRefs(product, template), optionSets, product);
  const colors = ((product as unknown as { colorAvailability?: Array<{ color: string }> } | null)?.colorAvailability ?? []).map((c) => c.color);
  const fresh = buildVariations({ product, cost, shipping, rule, variationSets, colors });
  const freshPrice = new Map<string, number>();
  for (const v of fresh) for (const o of v.options) if (typeof o.sellingPrice === "number") freshPrice.set(`${v.name}\u0000${o.label}`, o.sellingPrice);

  // Taslagin yapisi korunur; fiyatlar satis fiyati cinsine indirilip guncellenir
  let unmatched = 0;
  const saleVars: PlannedVariation[] | null = draft.variations?.length
    ? draft.variations.map((v) => ({
        ...v,
        options: v.options.map((o) => {
          const next = { ...o };
          if (typeof o.sellingPrice === "number") {
            const f = freshPrice.get(`${v.name}\u0000${o.label}`);
            if (f == null) unmatched++;
            next.sellingPrice = f ?? r2(o.sellingPrice / oldFactor);
          }
          if (typeof o.priceAdjust === "number" && o.priceAdjust !== 0) next.priceAdjust = r2(o.priceAdjust / oldFactor);
          return next;
        }),
      }))
    : null;

  const entry = pricing.entryVariant !== undefined ? pricing.entryVariant : (rules.entryVariant ?? null);
  const entryCombos = saleVars ? entryComboPrices(saleVars, salePrice, rule, entry) : [];
  const cheapest = entryCombos.reduce<(typeof entryCombos)[number] | null>((m, c) => (!m || c.price < m.price ? c : m), null);
  const entryProfit = cheapest ? etsyProfit(cheapest.price, cheapest.base - shipping, shipping).net : null;

  const newVariations = saleVars ? scaleVariationPrices(saleVars, LIST_PRICE_FACTOR) : null;
  const comboPrices = entryCombos.map((c) => ({ match: c.match, price: r2(c.price * LIST_PRICE_FACTOR) }));
  const { comboPrices: oldCombos, ...restAttrs } = attrs as { comboPrices?: Array<{ price: number }> };
  const oldEntry = oldCombos?.length ? Math.min(...oldCombos.map((c) => c.price)) : null;
  const newEntry = comboPrices.length ? Math.min(...comboPrices.map((c) => c.price)) : null;

  const oldRange = priceRange(draft.variations);
  const newRange = priceRange(newVariations);
  const varsChanged = (draft.variations ?? []).some((v, i) =>
    v.options.some((o, j) => differs(o.sellingPrice, newVariations?.[i]?.options[j]?.sellingPrice) || differs(o.priceAdjust, newVariations?.[i]?.options[j]?.priceAdjust)));
  const changed = differs(draft.price, listPrice) || varsChanged
    || (oldCombos?.length ?? 0) !== comboPrices.length
    || comboPrices.some((c, i) => differs(oldCombos?.[i]?.price, c.price));

  const skip = !product ? "Kaynak ürün katalogta bulunamadı"
    : cost <= 0 ? "Ürünün maliyeti yok"
    : undefined;

  return {
    draft, skip,
    oldCost: supplier.cost ?? null, newCost: cost,
    oldShipping: supplier.shipping ?? null, newShipping: shipping,
    oldRule, newRule: rule.label,
    oldPrice: draft.price, newPrice: listPrice,
    oldRange, newRange, oldEntry, newEntry,
    profit, marginPct: analysis.marginPct, entryProfit, unmatched,
    changed: !skip && changed,
    patch: {
      price: listPrice,
      variations: newVariations,
      attributes: {
        ...restAttrs,
        ...(comboPrices.length ? { comboPrices } : {}),
        supplier: { ...supplier, cost, shipping },
        pricing: {
          ...(({ multiplier: _m, commissionPercent: _c, marginPct: _p, ...rest }) => rest)(pricing),
          ...(useMargin != null ? { marginPct: useMargin } : { multiplier: pricing.multiplier }),
          salePrice, profit: r2(profit), etsyFees: r2(analysis.fees.total),
          listPriceFactor: LIST_PRICE_FACTOR, listPrice,
          entryVariant: entry, entryPrice: cheapest?.price ?? null, entryProfit: entryProfit != null ? r2(entryProfit) : null,
          recalculatedAt: new Date().toISOString(),
        },
      },
    },
  };
}
